<template>
    <!-- 새 벤더코드 참여 요청 (#442) — /reg/join 요청을 레벨 4 가 승인(회사+벤더코드 생성)·반려한다 -->
    <b-container fluid>
        <admin-page-header title="벤더코드 참여 요청" description="업체코드가 없는 업체의 참여 요청을 확인합니다. 승인하면 회사와 벤더코드가 만들어지고, 요청자에게 신청 링크가 메일로 갑니다."/>

        <b-card no-body>
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <b-form-radio-group :checked="status" :options="statusOptions" buttons button-variant="outline-primary"
                                        size="sm" :disabled="loading" @change="onStatusChange"/>
                    <b-spinner v-if="loading" small variant="primary"/>
                </div>
            </template>
            <PageEmptyComponent v-if="loadError" compact :error="loadError" @retry="load"/>
            <b-table v-else :items="rows" :fields="fields" show-empty empty-text="요청이 없습니다." small hover responsive class="mb-0">
                <template #cell(created_at)="{ value }">{{ formatDate(value) }}</template>
                <template #cell(applicant)="{ item }">
                    <div>{{ item.applicant_name }}</div>
                    <div class="small text-secondary">{{ item.applicant_email }}</div>
                </template>
                <template #cell(status)="{ value }">
                    <b-badge :variant="statusVariant[value]">{{ statusLabel[value] }}</b-badge>
                </template>
                <template #cell(actions)="{ item }">
                    <b-btn size="sm" variant="outline-primary" @click="openDetail(item.id)">보기</b-btn>
                </template>
            </b-table>
        </b-card>

        <b-modal v-model="detailOpen" :title="detail ? `${detail.company_name} 참여 요청` : '참여 요청'" size="lg" hide-footer
                 :no-close-on-esc="acting" :no-close-on-backdrop="acting" :hide-header-close="acting"
                 @hide="onDetailHide" @hidden="onDetailHidden"
        >
            <div v-if="detail">
                <b-table-simple small bordered class="mb-3">
                    <b-tbody>
                        <b-tr><b-th class="w-25">프로젝트 코드</b-th><b-td>{{ detail.project_code }}</b-td></b-tr>
                        <b-tr><b-th>기업명</b-th><b-td>{{ detail.company_name }}</b-td></b-tr>
                        <b-tr><b-th>사업자등록번호</b-th><b-td>{{ detail.business_no }}</b-td></b-tr>
                        <b-tr><b-th>국가</b-th><b-td>{{ detail.country || '-' }}</b-td></b-tr>
                        <b-tr><b-th>담당자</b-th><b-td>{{ detail.applicant_name }} / {{ detail.applicant_email }} / {{ detail.applicant_tel || '-' }}</b-td></b-tr>
                        <b-tr><b-th>요청 사항</b-th><b-td class="text-pre-wrap">{{ detail.memo || '-' }}</b-td></b-tr>
                        <b-tr><b-th>요청 화면 / 언어</b-th><b-td>{{ detail.origin || '-' }} / {{ detail.lang }}</b-td></b-tr>
                        <b-tr><b-th>요청일</b-th><b-td>{{ formatDate(detail.created_at) }}</b-td></b-tr>
                        <b-tr><b-th>상태</b-th><b-td><b-badge :variant="statusVariant[detail.status]">{{ statusLabel[detail.status] }}</b-badge></b-td></b-tr>
                        <b-tr v-if="detail.status === 'approved'"><b-th>연결 회사 / 벤더코드</b-th><b-td>{{ detail.company_id }} / {{ detail.vendor_code }}</b-td></b-tr>
                        <b-tr v-if="detail.status === 'rejected'"><b-th>반려 사유</b-th><b-td class="text-pre-wrap">{{ detail.reject_reason }}</b-td></b-tr>
                    </b-tbody>
                </b-table-simple>

                <template v-if="detail.status === 'pending'">
                    <h6 class="fw-600">회사 연결</h6>
                    <b-form-radio-group v-model="approve.company_id" stacked class="mb-3">
                        <b-form-radio :value="null">새 회사로 만들기 — {{ detail.company_name }} ({{ detail.business_no }})</b-form-radio>
                        <b-form-radio v-for="c in detail.candidates" :key="c.id" :value="c.id" :disabled="c.has_seat_in_project">
                            기존 회사 #{{ c.id }} {{ c.company_name }} ({{ c.company_code || '번호 없음' }}{{ c.country ? `, ${c.country}` : '' }})
                            <b-badge v-if="isBusinessNoMatch(c)" variant="success" class="ml-1">사업자번호 일치</b-badge>
                            <b-badge v-if="c.company_name === detail.company_name" variant="info" class="ml-1">기업명 일치</b-badge>
                            <span v-if="c.has_seat_in_project" class="text-danger small"> — 이 프로젝트에 이미 벤더코드 있음</span>
                        </b-form-radio>
                    </b-form-radio-group>
                    <p v-if="!detail.candidates.length" class="small text-secondary mt-n2">같은 사업자등록번호·기업명의 기존 회사가 없습니다.</p>

                    <b-form-group label="벤더코드" label-for="join-vendor-code" description="자동 발급값입니다. 필요하면 바꿔 입력하세요.">
                        <b-form-input id="join-vendor-code" v-model="approve.vendor_code" maxlength="45" :disabled="acting"/>
                    </b-form-group>
                    <div class="text-right mb-4">
                        <b-btn variant="primary" :disabled="acting || !approve.vendor_code.trim()" @click="doApprove">
                            <b-spinner v-if="acting" small class="mr-1"/>승인
                        </b-btn>
                    </div>

                    <h6 class="fw-600">반려</h6>
                    <b-textarea v-model="rejectReason" rows="2" placeholder="반려 사유 (요청자에게 메일로 전달됩니다)" class="mb-2" :disabled="acting"/>
                    <div class="text-right">
                        <b-btn variant="outline-danger" :disabled="acting || !rejectReason.trim()" @click="doReject">반려</b-btn>
                    </div>
                </template>

                <b-alert :show="!!actionMessage" :variant="actionVariant" class="mt-3 mb-0 text-break text-pre-wrap">{{ actionMessage }}</b-alert>
                <!-- 승인 직후 신청 링크. 메일이 실패하면 요청자에게 직접 전달해야 한다 (다시 열면 볼 수 없음) -->
                <b-form-group v-if="lastInvite" label="신청 링크" label-for="join-invite-url" class="mt-3 mb-0"
                              :description="lastInvite.mail_sent ? '요청자에게 메일로 보냈습니다.' : '메일이 가지 않았습니다. 복사해 요청자에게 직접 전달해주세요. 창을 닫으면 다시 볼 수 없습니다.'"
                >
                    <b-input-group size="sm">
                        <b-form-input id="join-invite-url" :value="lastInvite.url" readonly/>
                        <b-input-group-append>
                            <b-btn variant="outline-primary" @click="copyInvite">복사</b-btn>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </div>
            <div v-else class="text-center py-4"><b-spinner variant="primary"/></div>
        </b-modal>
    </b-container>
</template>

<script>
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import { getErrorMessage } from '@core/utils/api/errorMessage';

// ?status= 값 (대기는 기본이라 키 생략, 전체는 all)
const STATUS_QUERY_VALUES = ['approved', 'rejected', 'all'];

const ERRORS = {
    Not_Pending_Request: '이미 처리된 요청입니다.',
    Duplicate_Company_Name: '같은 기업명의 회사가 이미 있습니다. 기존 회사를 선택해주세요.',
    Already_Registered_Vendor: '선택한 회사는 이 프로젝트에 이미 벤더코드가 있습니다.',
    Duplicate_Vendor_Code: '이 프로젝트에 같은 벤더코드가 이미 있습니다. 다른 값을 입력해주세요.',
    Project_Has_No_Period: '프로젝트에 기간(period)이 없어 새 회사를 만들 수 없습니다.',
    Not_Found_Project: '프로젝트를 찾을 수 없습니다.',
    Not_Found_Company: '선택한 회사를 찾을 수 없습니다.',
};

export default {
    name: 'VendorJoinRequestManagement',
    components: { PageEmptyComponent },
    data() {
        return {
            status: 'pending',
            statusOptions: [
                {text: '대기', value: 'pending'}, {text: '승인', value: 'approved'},
                {text: '반려', value: 'rejected'}, {text: '전체', value: null},
            ],
            statusLabel: {pending: '대기', approved: '승인', rejected: '반려'},
            statusVariant: {pending: 'warning', approved: 'success', rejected: 'secondary'},
            fields: [
                {key: 'created_at', label: '요청일'},
                {key: 'project_code', label: '프로젝트 코드'},
                {key: 'company_name', label: '기업명'},
                {key: 'business_no', label: '사업자등록번호'},
                {key: 'applicant', label: '담당자'},
                {key: 'status', label: '상태'},
                {key: 'actions', label: ''},
            ],
            rows: [],
            loading: false,
            loadError: '',
            loadSeq: 0,
            detailOpen: false,
            detail: null,
            // 열려 있는 상세의 요청 id (?request= 와 맞춰 중복 조회를 막는다)
            openRequestId: null,
            approve: {company_id: null, vendor_code: ''},
            rejectReason: '',
            acting: false,
            actionMessage: null,
            actionVariant: 'success',
            // 승인 응답의 신청 링크 {url, mail_sent}. 상세 재조회 응답에는 없어 창을 닫으면 사라진다
            lastInvite: null,
            inviteCopied: false,
            closeConfirmed: false,
        };
    },
    watch: {
        '$route.query.status'() {
            this.syncStatusFromRoute();
        },
        '$route.query.request'() {
            this.syncRequestFromRoute();
        },
    },
    mounted() {
        this.syncStatusFromRoute();
        this.syncRequestFromRoute();
    },
    methods: {
        formatDate(value) {
            return value ? value.replace('T', ' ').slice(0, 16) : '-';
        },
        // 상태 필터는 URL 이 기준. 허용값 밖이면 키를 지워(=대기) 보정한다
        syncStatusFromRoute() {
            const s = this.$route.query.status;
            if (s !== undefined && !STATUS_QUERY_VALUES.includes(s)) {
                this.replaceQuery({status: undefined});
                return;
            }
            this.status = s === undefined ? 'pending' : (s === 'all' ? null : s);
            this.load();
        },
        onStatusChange(value) {
            const query = {...this.$route.query};
            delete query.request;
            if (value === 'pending') {
                delete query.status;
            } else {
                query.status = value || 'all';
            }
            this.$router.push({query}).catch(() => {});
        },
        syncRequestFromRoute() {
            // 승인·반려 처리 중에는 다른 요청으로 바뀌지 않게 열린 요청으로 URL 을 되돌린다 (결과가 섞이지 않도록)
            if (this.acting) {
                this.keepRequestQuery();
                return;
            }
            const raw = this.$route.query.request;
            if (raw === undefined) {
                // 뒤로가기 등으로 키가 빠지면 창도 닫는다
                if (this.detailOpen) this.detailOpen = false;
                return;
            }
            const id = Number(raw);
            if (!Number.isInteger(id) || id <= 0) {
                this.replaceQuery({request: undefined});
                return;
            }
            if (id !== this.openRequestId) this.openDetail(id);
        },
        // 닫기를 막았을 때 등 열린 상세와 URL 이 어긋나면 ?request= 를 되돌린다
        keepRequestQuery() {
            if (this.openRequestId !== null && this.$route.query.request !== String(this.openRequestId)) {
                this.replaceQuery({request: String(this.openRequestId)});
            }
        },
        // patch 의 undefined 값은 키를 지운다
        replaceQuery(patch) {
            const query = {...this.$route.query, ...patch};
            Object.keys(patch).forEach(key => {
                if (patch[key] === undefined) delete query[key];
            });
            this.$router.replace({query}).catch(() => {});
        },
        async load() {
            // 상태를 빠르게 바꾸면 늦게 온 이전 상태의 응답을 버린다
            const request = ++this.loadSeq;
            this.loading = true;
            try {
                const {data} = await this.$axios.get('/vendor-join-requests', {params: this.status ? {status: this.status} : {}});
                if (request !== this.loadSeq) return;
                this.rows = data;
                this.loadError = '';
            } catch (e) {
                if (request !== this.loadSeq) return;
                this.rows = [];
                if (e?.response?.status === 401) return;
                // 빈 목록('요청이 없습니다')과 구분해 승인 대기 건을 놓치지 않게 한다
                this.loadError = getErrorMessage(e, '참여 요청 목록을 불러오지 못했습니다.');
            } finally {
                if (request === this.loadSeq) this.loading = false;
            }
        },
        async openDetail(id) {
            this.openRequestId = id;
            this.detail = null;
            this.actionMessage = null;
            this.rejectReason = '';
            this.lastInvite = null;
            this.inviteCopied = false;
            this.detailOpen = true;
            if (this.$route.query.request !== String(id)) this.replaceQuery({request: String(id)});
            try {
                const {data} = await this.$axios.get(`/vendor-join-requests/${id}`);
                // 기다리는 사이 창을 닫았거나 다른 요청을 열었으면 버린다
                if (this.openRequestId !== id) return;
                this.detail = data;
                // 사업자번호·기업명이 모두 같고 선택 가능한 기존 회사만 기본 선택 (번호만 같으면 배지로 알리고 직접 고르게 함)
                const matched = (data.candidates || []).find(c => this.isBusinessNoMatch(c, data) && c.company_name === data.company_name && !c.has_seat_in_project);
                this.approve = {company_id: matched ? matched.id : null, vendor_code: data.suggested_vendor_code || ''};
            } catch (e) {
                if (this.openRequestId !== id) return;
                this.detailOpen = false;
                if (e?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '참여 요청', e?.response?.status === 404
                    ? `참여 요청 #${id} 을(를) 찾을 수 없습니다.`
                    : getErrorMessage(e, '참여 요청을 불러오지 못했습니다.'));
            }
        },
        // 처리 중에는 닫지 않고, 메일이 안 간 신청 링크를 아직 복사하지 않았으면 한 번 더 묻는다
        onDetailHide(bvEvent) {
            if (this.acting) {
                bvEvent.preventDefault();
                this.keepRequestQuery();
                return;
            }
            if (this.closeConfirmed || !this.lastInvite || this.lastInvite.mail_sent || this.inviteCopied) return;
            bvEvent.preventDefault();
            this.keepRequestQuery();
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '신청 링크 미전달',
                message: '승인 메일이 발송되지 않았습니다. 신청 링크를 아직 요청자에게 전달하지 않았다면 닫기 전에 복사하세요. 창을 닫으면 링크를 다시 볼 수 없습니다. 닫을까요?',
            }).then((ok) => {
                if (!ok) return;
                this.closeConfirmed = true;
                this.detailOpen = false;
            });
        },
        onDetailHidden() {
            // 닫히는 동안 다른 요청을 열었으면 그 요청의 상태를 지우지 않는다
            if (this.detailOpen) return;
            this.openRequestId = null;
            this.closeConfirmed = false;
            this.lastInvite = null;
            if (this.$route.query.request !== undefined) this.replaceQuery({request: undefined});
        },
        async copyInvite() {
            try {
                await navigator.clipboard.writeText(this.lastInvite.url);
                this.inviteCopied = true;
                this.$EventBus.$emit('toast-success', '신청 링크', '신청 링크를 복사했습니다.');
            } catch (e) {
                this.$EventBus.$emit('toast-failure', '신청 링크', '복사하지 못했습니다. 링크를 직접 선택해 복사해주세요.');
            }
        },
        isBusinessNoMatch(c, detail = this.detail) {
            const normalize = s => (s || '').replace(/[-\s]/g, '').toUpperCase();
            return !!normalize(detail.business_no) && normalize(c.company_code) === normalize(detail.business_no);
        },
        showResult(message, variant = 'success') {
            this.actionMessage = message;
            this.actionVariant = variant;
        },
        errorMessage(e) {
            const message = e?.response?.data?.message;
            return ERRORS[message] || `처리하지 못했습니다. (${message || e?.message})`;
        },
        async doApprove() {
            const d = this.detail;
            const existing = (d.candidates || []).find(c => c.id === this.approve.company_id);
            const target = existing ? `기존 회사 #${existing.id} ${existing.company_name}` : `새 회사 "${d.company_name}" (${d.business_no})`;
            const nameMismatch = !!existing && existing.company_name !== d.company_name;
            const dupWarn = !existing && d.candidates.some(c => this.isBusinessNoMatch(c))
                ? '\n같은 사업자번호의 기존 회사가 있습니다.' : '';
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '참여 요청 승인',
                message: this.$createElement('div', {style: {whiteSpace: 'pre-line'}}, [
                    `${d.project_code}에 ${target}로 벤더코드 ${this.approve.vendor_code.trim()}를 만들고 승인합니다.
신청 링크 메일이 ${d.applicant_email}로 발송됩니다. 승인 후 되돌릴 수 없습니다.${dupWarn}`,
                    nameMismatch ? this.$createElement('div', {class: 'text-danger mt-2'}, `요청 기업명: ${d.company_name} / 선택 회사: ${existing.company_name} — 이름이 다릅니다`) : null
                ])
            });
            if (!ok) return;
            this.acting = true;
            try {
                const {data} = await this.$axios.post(`/vendor-join-requests/${this.detail.id}/approve`, {
                    company_id: this.approve.company_id,
                    vendor_code: this.approve.vendor_code.trim(),
                });
                const mail = data.mail_sent ? '요청자에게 신청 링크 메일을 보냈습니다.' : '메일 발송에 실패했습니다. 아래 링크를 요청자에게 직접 전달해주세요.';
                this.lastInvite = data.invite_url ? {url: data.invite_url, mail_sent: !!data.mail_sent} : null;
                await this.reloadDetail();
                this.showResult(`승인 완료 — 벤더코드 ${data.vendor_code}. ${mail}`, data.mail_sent ? 'success' : 'warning');
            } catch (e) {
                this.showResult(this.errorMessage(e), 'danger');
            } finally {
                this.acting = false;
            }
        },
        async doReject() {
            const d = this.detail;
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'error',
                title: '참여 요청 반려',
                message: `${d.company_name} 요청을 반려하고 ${d.applicant_email}로 반려 메일을 보냅니다. 같은 프로젝트·사업자등록번호로는 다시 요청할 수 없습니다.`
            });
            if (!ok) return;
            this.acting = true;
            try {
                const {data} = await this.$axios.post(`/vendor-join-requests/${this.detail.id}/reject`, {reason: this.rejectReason.trim()});
                await this.reloadDetail();
                this.showResult(data.mail_sent ? '반려했습니다. 요청자에게 메일을 보냈습니다.' : '반려했습니다. 메일 발송에 실패했습니다.',
                    data.mail_sent ? 'success' : 'warning');
            } catch (e) {
                this.showResult(this.errorMessage(e), 'danger');
            } finally {
                this.acting = false;
            }
        },
        async reloadDetail() {
            const {data} = await this.$axios.get(`/vendor-join-requests/${this.detail.id}`);
            this.detail = data;
            this.load();
        },
    },
};
</script>

<style scoped>
.text-pre-wrap {
    white-space: pre-wrap;
}
</style>
